<template>
  <div class="flex h-10 flex-shrink-0 rounded-xl bg-secondary" role="group" aria-label="接種操作">
    <button
      @click="emit('confirm')"
      class="h-full px-1.5 min-[360px]:px-3 rounded-l-xl text-xs font-bold whitespace-nowrap active:bg-black/5"
    >
      確認接種
    </button>
    <button
      ref="menuTrigger"
      @click="openMenu"
      @keydown.down.prevent="openMenu"
      class="w-7 h-full flex items-center justify-center rounded-r-xl border-l border-black/10 active:bg-black/5"
      aria-label="更多接種操作"
      aria-haspopup="menu"
      :aria-expanded="menuOpen"
      :aria-controls="menuId"
    >
      <svg class="w-3 h-3" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
        <path d="m4 6 4 4 4-4" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>
  </div>

  <!-- 浮層不占卡片高度，也不會被卡片的 overflow-hidden 裁切。 -->
  <Teleport to="body">
    <div
      v-if="menuOpen"
      class="fixed inset-0 z-40"
      @click.self="closeMenu(true)"
      @keydown.esc.stop.prevent="closeMenu(true)"
      @keydown.tab="closeMenu(true)"
    >
      <div
        ref="menuPanel"
        :id="menuId"
        role="menu"
        aria-label="其他接種操作"
        class="fixed w-56 max-w-[calc(100vw-1rem)] rounded-2xl border border-primary/40 bg-background p-1.5 shadow-[0_8px_28px_rgba(113,79,69,0.16)]"
        :style="menuPosition"
      >
        <button
          ref="skipOption"
          role="menuitem"
          aria-label="不接種"
          @click="chooseSkip"
          @keydown.down.prevent
          @keydown.up.prevent
          class="w-full flex items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors hover:bg-primary/15 active:bg-primary/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <span class="w-9 h-9 flex-shrink-0 rounded-full bg-primary/25 flex items-center justify-center text-[#9B6651]" aria-hidden="true">
            <svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
              <path d="m3 3 18 18M10.6 10.6a2 2 0 0 0 2.8 2.8M9.8 5.3A10 10 0 0 1 12 5c5.5 0 9 7 9 7a17 17 0 0 1-2.4 3.5M6.2 6.2A18 18 0 0 0 3 12s3.5 7 9 7a10 10 0 0 0 5.8-1.8" />
            </svg>
          </span>
          <span class="min-w-0">
            <span class="block text-sm font-bold text-[#714F45]">不接種</span>
            <span class="block mt-0.5 text-xs text-[#94766A]">移至已隱藏項目</span>
          </span>
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { nextTick, onUnmounted, ref, useId } from 'vue'

const emit = defineEmits<{ confirm: []; skip: [] }>()
const menuId = useId()
const menuTrigger = ref<HTMLButtonElement | null>(null)
const menuPanel = ref<HTMLDivElement | null>(null)
const skipOption = ref<HTMLButtonElement | null>(null)
const menuOpen = ref(false)
const menuPosition = ref({ left: '0px', top: '0px' })

const dismissMenu = () => closeMenu()

const closeMenu = (restoreFocus = false) => {
  menuOpen.value = false
  window.removeEventListener('scroll', dismissMenu, true)
  window.removeEventListener('resize', dismissMenu)
  if (restoreFocus) menuTrigger.value?.focus({ preventScroll: true })
}

const openMenu = async () => {
  const trigger = menuTrigger.value
  if (!trigger) return

  const rect = trigger.getBoundingClientRect()
  // 先讓浮層參與排版，再依實際尺寸定位，避免文字或字級改變時超出螢幕。
  menuOpen.value = true
  await nextTick()
  if (!menuOpen.value || !menuPanel.value) return

  const width = menuPanel.value.offsetWidth
  const height = menuPanel.value.offsetHeight
  const gap = 8
  const left = Math.max(gap, Math.min(rect.right - width, window.innerWidth - width - gap))
  const top = rect.bottom + gap + height <= window.innerHeight
    ? rect.bottom + gap
    : Math.max(gap, rect.top - height - gap)

  menuPosition.value = { left: `${left}px`, top: `${top}px` }
  window.addEventListener('scroll', dismissMenu, true)
  window.addEventListener('resize', dismissMenu)
  await nextTick()
  skipOption.value?.focus({ preventScroll: true })
}

const chooseSkip = () => {
  closeMenu()
  emit('skip')
}

onUnmounted(() => closeMenu())
</script>
